import React, { useState, useCallback, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, Zap, Star, Check, X, ChevronRight, Trophy, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../config';

const CATEGORY_LABELS = {
  airports: '✈️ Airports',
  airlines: '🏢 Airlines',
  geography: '🌍 Geography',
  trivia: '🧠 Trivia',
  flyNOut: '🚀 Fly N Out',
};

const DIFFICULTY_COLORS = {
  easy: '#10B981',
  medium: '#F59E0B',
  hard: '#EF4444',
};

function QuizQuestion({ question, index, total, onAnswer, answered }) {
  return (
    <motion.div
      key={question.question_id}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-4"
    >
      {/* Progress */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full rounded-full bg-[#FF4500] transition-all duration-500"
            style={{ width: `${((index) / total) * 100}%` }}
          />
        </div>
        <span className="text-xs text-muted-foreground flex-shrink-0">{index + 1} / {total}</span>
      </div>

      {/* Category + difficulty */}
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-muted-foreground font-medium">
          {CATEGORY_LABELS[question.category] || question.category}
        </span>
        <span
          className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold"
          style={{
            background: `${DIFFICULTY_COLORS[question.difficulty] || '#6B7280'}20`,
            color: DIFFICULTY_COLORS[question.difficulty] || '#6B7280',
          }}
        >
          {question.difficulty}
        </span>
      </div>

      {/* Question */}
      <p className="text-sm font-semibold leading-relaxed" style={{ fontFamily: 'Space Grotesk' }}>
        {question.question}
      </p>

      {/* Options */}
      <div className="grid grid-cols-1 gap-2">
        {question.options.map((opt, i) => {
          const state = answered
            ? i === answered.correct
              ? 'correct'
              : i === answered.given
              ? 'wrong'
              : 'dim'
            : 'default';
          return (
            <button
              key={i}
              onClick={() => !answered && onAnswer(i)}
              disabled={!!answered}
              className={`text-left p-3 rounded-xl border text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                state === 'correct'
                  ? 'border-[#10B981] bg-[#10B981]/10 text-[#10B981]'
                  : state === 'wrong'
                  ? 'border-red-400 bg-red-50 dark:bg-red-950/20 text-red-600'
                  : state === 'dim'
                  ? 'border-border bg-muted/30 text-muted-foreground opacity-60'
                  : 'border-border hover:border-[#FF4500] hover:bg-[#FF4500]/5 cursor-pointer'
              }`}
              data-testid={`quiz-option-${i}`}
            >
              <span
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                  state === 'correct' ? 'bg-[#10B981] text-white' :
                  state === 'wrong' ? 'bg-red-400 text-white' :
                  'bg-muted text-muted-foreground'
                }`}
              >
                {state === 'correct' ? <Check className="w-3.5 h-3.5" /> :
                 state === 'wrong' ? <X className="w-3.5 h-3.5" /> :
                 String.fromCharCode(65 + i)}
              </span>
              {opt}
            </button>
          );
        })}
      </div>
    </motion.div>
  );
}

function QuizResult({ score, total, xpEarned, onClose }) {
  const perfect = score === total;
  const pct = Math.round((score / total) * 100);
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center space-y-5 py-4"
    >
      <div className="text-6xl">{perfect ? '🏆' : score >= 3 ? '🌟' : '✈️'}</div>
      <div>
        <h3 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
          {perfect ? 'Perfect Score!' : score >= 3 ? 'Great Job!' : 'Keep Flying!'}
        </h3>
        <p className="text-muted-foreground text-sm mt-1">
          {score} out of {total} correct
        </p>
      </div>

      {/* Score ring */}
      <div className="flex justify-center">
        <div className="relative w-24 h-24">
          <svg width="96" height="96" style={{ transform: 'rotate(-90deg)' }}>
            <circle cx="48" cy="48" r="40" fill="none" stroke="currentColor" strokeWidth="8" className="text-muted opacity-20" />
            <motion.circle
              cx="48" cy="48" r="40" fill="none"
              stroke={perfect ? '#D97706' : score >= 3 ? '#10B981' : '#FF4500'}
              strokeWidth="8" strokeLinecap="round"
              strokeDasharray={251.2}
              initial={{ strokeDashoffset: 251.2 }}
              animate={{ strokeDashoffset: 251.2 - (pct / 100) * 251.2 }}
              transition={{ duration: 1, ease: 'easeOut' }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{pct}%</span>
          </div>
        </div>
      </div>

      {/* XP earned */}
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#FF4500]/10 border border-[#FF4500]/20">
        <Zap className="w-4 h-4 text-[#FF4500]" />
        <span className="font-bold text-[#FF4500]">+{xpEarned} XP earned</span>
        {perfect && <Star className="w-4 h-4 text-amber-400 fill-amber-400" />}
      </div>
      {perfect && (
        <p className="text-xs text-muted-foreground">Includes +10 XP perfect score bonus!</p>
      )}

      <Button className="fly-btn-primary w-full" onClick={onClose} data-testid="quiz-done-btn">
        Done
      </Button>
    </motion.div>
  );
}

export default function QuizWidget({ onXpGained, autoOpen = false, hideCard = false }) {
  const [open, setOpen] = useState(autoOpen);
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState([]); // list of chosen indices
  const [answered, setAnswered] = useState(null); // { given, correct } for current question
  const [result, setResult] = useState(null);
  const [quizStatus, setQuizStatus] = useState(null); // null | 'available' | 'done'

  const loadQuiz = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quiz/today`, { withCredentials: true });
      if (res.data.already_completed) {
        setResult({ score: res.data.score, total: res.data.total, xpEarned: res.data.xp_earned });
        setQuizStatus('done');
      } else {
        setSessionData(res.data);
        setQuizStatus('available');
        setCurrentQ(0);
        setAnswers([]);
        setAnswered(null);
        setResult(null);
      }
    } catch (e) {
      if (e.response?.status !== 401) toast.error('Could not load quiz');
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-check quiz status on mount (silently, no dialog)
  useEffect(() => {
    axios.get(`${API}/quiz/today`, { withCredentials: true })
      .then(res => {
        if (res.data.already_completed) {
          setResult({ score: res.data.score, total: res.data.total, xpEarned: res.data.xp_earned });
          setQuizStatus('done');
        } else {
          setQuizStatus('available');
        }
      })
      .catch(() => {}); // Silent fail if not logged in
  }, []);

  const handleOpen = () => {
    setOpen(true);
    if (!sessionData) loadQuiz();
  };

  const handleAnswer = async (optionIndex) => {
    if (!sessionData) return;
    const questions = sessionData.questions;
    const q = questions[currentQ];
    const newAnswers = [...answers, optionIndex];
    setAnswers(newAnswers);

    // We don't know correct_index client-side until submission, so we submit after last question
    if (newAnswers.length < questions.length) {
      // Show brief feedback (we'll reveal on final submit), then move to next question
      setAnswered({ given: optionIndex, correct: optionIndex }); // temporary "selected" state
      setTimeout(() => {
        setAnswered(null);
        setCurrentQ(i => i + 1);
      }, 600);
    } else {
      // Last question — submit all answers
      setAnswered({ given: optionIndex, correct: optionIndex });
      setTimeout(async () => {
        try {
          const res = await axios.post(`${API}/quiz/submit`, {
            session_id: sessionData.session_id,
            answers: newAnswers,
          }, { withCredentials: true });
          setResult({
            score: res.data.score,
            total: res.data.total,
            xpEarned: res.data.xp_earned,
            perfect: res.data.perfect,
            results: res.data.results,
          });
          setQuizStatus('done');
          if (onXpGained && res.data.xp_earned > 0) onXpGained(res.data.xp_earned);
        } catch (e) {
          toast.error('Submission failed');
        }
      }, 700);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  if (hideCard) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="quiz-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-[#7C3AED]" />
              Daily Travel Quiz
            </DialogTitle>
          </DialogHeader>
          {loading && (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          )}
          {!loading && result && quizStatus === 'done' && (
            <QuizResult score={result.score} total={result.total} xpEarned={result.xpEarned} onClose={handleClose} />
          )}
          {!loading && sessionData && quizStatus === 'available' && !result && (
            <AnimatePresence mode="wait">
              <QuizQuestion key={currentQ} question={sessionData.questions[currentQ]} index={currentQ} total={sessionData.questions.length} onAnswer={handleAnswer} answered={answered} />
            </AnimatePresence>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Card
        className="fly-card p-4 cursor-pointer hover:shadow-md transition-shadow group"
        onClick={handleOpen}
        data-testid="quiz-widget"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#7C3AED]/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
            <Brain className="w-5 h-5 text-[#7C3AED]" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk' }}>
              Daily Travel Quiz
            </p>
            <p className="text-[10px] text-muted-foreground">
              5 questions · Earn up to 35 XP
            </p>
          </div>
          <div className="flex items-center gap-1 text-[10px] font-bold text-[#FF4500]">
            <Zap className="w-3 h-3" /> +35 XP
          </div>
        </div>
        {quizStatus === 'done' && result && (
          <div className="mt-2 pt-2 border-t border-border flex items-center gap-2 text-xs text-muted-foreground">
            <Trophy className="w-3 h-3 text-amber-500" />
            Today: {result.score}/{result.total} · +{result.xpEarned} XP earned
          </div>
        )}
        {quizStatus === null && (
          <p className="text-[10px] text-[#FF4500] mt-1">Click to start →</p>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="quiz-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-[#7C3AED]" />
              Daily Travel Quiz
            </DialogTitle>
          </DialogHeader>

          {loading && (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {!loading && result && quizStatus === 'done' && (
            <QuizResult
              score={result.score}
              total={result.total}
              xpEarned={result.xpEarned}
              onClose={handleClose}
            />
          )}

          {!loading && sessionData && quizStatus === 'available' && !result && (
            <AnimatePresence mode="wait">
              <QuizQuestion
                key={currentQ}
                question={sessionData.questions[currentQ]}
                index={currentQ}
                total={sessionData.questions.length}
                onAnswer={handleAnswer}
                answered={answered}
              />
            </AnimatePresence>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
